import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Box,
  Typography,
  Chip,
  Button,
  Divider,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  useTheme,
  useMediaQuery,
  Fade,
  Paper,
  Avatar,
  Skeleton
} from '@mui/material';
import { motion } from 'framer-motion';
// Adjust path if necessary based on your actual structure
import { useSectionVisibility } from '../../../hooks';
import { useTranslation } from 'react-i18next';
// Adjust path if necessary based on your actual structure
import {
  colors,
  containerVariants,
  itemVariants
} from '../../styled/StyledComponents';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ShareIcon from '@mui/icons-material/Share';
import { styled, keyframes } from '@mui/material/styles';

// Animations (Keep as is)
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// Styled components (Keep as is)
const ArticleCard = styled(motion.div)(({ theme }) => ({
  height: '100%',
  position: 'relative',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  background: colors.cardBackground,
  border: `1px solid ${colors.cardBorder}`,
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
    '& .article-image': {
      transform: 'scale(1.05)',
    },
    '& .read-more-btn': {
      color: colors.secondary,
      '& .arrow-icon': {
        transform: 'translateX(4px)',
      },
    },
  },
}));

const ArticleImage = styled(Box)({
  position: 'relative',
  paddingTop: '56.25%', // 16:9 aspect ratio
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'transform 0.5s ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%)',
  },
});

const CategoryChip = styled(Chip)(({ theme, variant }) => ({
  backgroundColor: variant === 'primary'
    ? `${colors.primary}20`
    : variant === 'secondary'
      ? `${colors.secondary}20`
      : `${colors.tertiary}20`,
  color: variant === 'primary'
    ? colors.primary
    : variant === 'secondary'
      ? colors.secondary
      : colors.tertiary,
  fontWeight: 600,
  borderRadius: '6px',
  '& .MuiChip-label': {
    padding: '0 10px',
  },
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: variant === 'primary'
      ? `${colors.primary}30`
      : variant === 'secondary'
        ? `${colors.secondary}30`
        : `${colors.tertiary}30`,
  },
}));

const ReadMoreButton = styled(Button)({
  color: colors.primary,
  padding: 0,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'transparent',
    color: colors.secondary,
  },
  '& .arrow-icon': {
    transition: 'transform 0.3s ease',
  },
});

const NewsListItemWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    '& .news-title': {
      color: colors.primary,
    },
    '& .read-more-icon': {
      opacity: 1,
      transform: 'translateX(0)',
    },
    '& .news-image': {
      transform: 'scale(1.05)',
    },
  },
}));

const DialogCloseButton = styled(IconButton)(({ theme, light }) => ({
  position: 'absolute',
  right: 16,
  top: 16,
  color: light ? 'white' : colors.text,
  backgroundColor: light ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.8)',
  zIndex: 10,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: light ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.9)',
    transform: 'rotate(90deg)',
  },
}));

// ArticleDialog Component (Keep as is)
const ArticleDialog = ({ article, open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 800);
      return () => clearTimeout(timer);
    } else {
      setIsLoading(true);
    }
  }, [open]);

  if (!article) return null;

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: colors.background,
          backgroundImage: 'none',
          overflow: 'hidden',
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)',
        }
      }}
      TransitionComponent={Fade}
    >
      <Box sx={{ position: 'relative' }}>
        {article.featured_image && (
          <Box
            sx={{
              width: '100%',
              height: { xs: '200px', sm: '300px' },
              backgroundImage: `url(${article.featured_image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.6) 100%)',
              },
            }}
          />
        )}
        <DialogCloseButton
          onClick={onClose}
          light={true}
          aria-label="Close dialog"
        >
          <CloseIcon />
        </DialogCloseButton>
      </Box>
      <DialogContent sx={{ p: { xs: 2, sm: 4 } }}>
        {isLoading ? (
          // Loading skeleton (Keep as is)
          <>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="rounded" width={80} height={24} />
              <Skeleton variant="text" width={120} />
              <Skeleton variant="text" width={100} />
            </Box>
            <Skeleton variant="text" width="80%" height={60} sx={{ mb: 3 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="95%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="90%" height={20} sx={{ mb: 3 }} />
            <Skeleton variant="rectangular" width="100%" height={200} sx={{ mb: 3 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="85%" height={20} sx={{ mb: 3 }} />
          </>
        ) : (
          // Actual Content (Keep as is)
          <>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <CategoryChip label={article.category} variant="primary" />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarTodayIcon sx={{ fontSize: 16, color: colors.textLight }} />
                <Typography variant="caption" sx={{ color: colors.textLight }}>
                  {t('news.publishedOn', { date: new Date(article.first_published_at).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' }) })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: colors.textLight }} />
                <Typography variant="caption" sx={{ color: colors.textLight }}>
                  {t('news.estimatedReadTime', { time: article.estimated_read_time })}
                </Typography>
              </Box>
            </Box>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: colors.text, fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' }, lineHeight: 1.2 }}>
              {article.title}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src="/images/team/daniel.png" alt="Author" sx={{ width: 40, height: 40 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.text }}>Daniel van Wassem</Typography>
                  <Typography variant="caption" sx={{ color: colors.textLight }}>CEO & Founder</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton size="small" sx={{ color: colors.textLight }}><BookmarkBorderIcon fontSize="small" /></IconButton>
                <IconButton size="small" sx={{ color: colors.textLight }}><ShareIcon fontSize="small" /></IconButton>
              </Box>
            </Box>
            <Box sx={{ color: colors.text, '& p': { marginBottom: '1.5em', fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.7, '&:last-child': { marginBottom: 0 } }, '& p[data-block-key]': { marginBottom: '1.5em', '&:last-child': { marginBottom: 0 } }, '& img': { maxWidth: '100%', height: 'auto', borderRadius: '8px', margin: '1.5em 0' } }} dangerouslySetInnerHTML={{ __html: article.body }} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ViewAllDialog Component (Keep as is)
const ViewAllDialog = ({ open, onClose, articles }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => { setIsLoading(false); }, 600);
      return () => clearTimeout(timer);
    } else {
      setIsLoading(true);
    }
  }, [open]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={() => onClose()} // Pass no arguments
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', background: colors.background, backgroundImage: 'none' } }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)' } }}
      TransitionComponent={Fade}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, borderBottom: `1px solid ${colors.cardBorder}` }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.text }}>{t('news.allArticles')}</Typography>
        <DialogCloseButton onClick={() => onClose()} aria-label="Close dialog"><CloseIcon /></DialogCloseButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {isLoading ? (
          // Loading skeleton (Keep as is)
          <Grid container spacing={3}>{ [...Array(6)].map((_, index) => ( <Grid item xs={12} md={6} lg={4} key={index}><Paper elevation={0} sx={{ borderRadius: '12px', overflow: 'hidden', border: `1px solid ${colors.cardBorder}`, height: '100%' }}><Skeleton variant="rectangular" width="100%" height={200} /><Box sx={{ p: 2 }}><Box sx={{ mb: 2, display: 'flex', gap: 1 }}><Skeleton variant="rounded" width={80} height={24} /><Skeleton variant="text" width={60} /></Box><Skeleton variant="text" width="90%" height={28} sx={{ mb: 1 }} /><Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} /><Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} /><Skeleton variant="text" width={100} height={20} /></Box></Paper></Grid> ))}</Grid>
        ) : (
          // Actual Content (Keep as is)
          <Grid container spacing={3}>
            {articles.map((article) => (
              <Grid item xs={12} md={6} lg={4} key={article.id}>
                <Paper elevation={0} sx={{ borderRadius: '12px', overflow: 'hidden', border: `1px solid ${colors.cardBorder}`, height: '100%', transition: 'all 0.3s ease', cursor: 'pointer', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)', '& .article-image': { transform: 'scale(1.05)' } } }} onClick={() => onClose(article)} >
                  <Box className="article-image" sx={{ height: 200, backgroundImage: article.featured_image ? `url(${article.featured_image})` : 'url(https://via.placeholder.com/400x225?text=News)', backgroundSize: 'cover', backgroundPosition: 'center', transition: 'transform 0.5s ease' }} />
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                      <CategoryChip label={article.category} size="small" variant={article.category === 'Technology' ? 'primary' : article.category === 'Business' ? 'secondary' : 'tertiary'} />
                      <Typography variant="caption" sx={{ color: colors.textLight, display: 'flex', alignItems: 'center', gap: 0.5 }}><CalendarTodayIcon sx={{ fontSize: 12 }} /> {new Date(article.first_published_at).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: colors.text, fontSize: '1.1rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.3, height: '2.6rem' }}>{article.title}</Typography>
                    <Typography variant="body2" sx={{ color: colors.textLight, mb: 2, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '4.5rem' }}>{article.excerpt}</Typography>
                    <ReadMoreButton endIcon={<ArrowForwardIcon className="arrow-icon" />}>{t('news.readArticle')}</ReadMoreButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

// FeaturedNewsCard Component (Keep as is)
const FeaturedNewsCard = ({ item, onReadMore }) => {
  const { t } = useTranslation();
  return (
    <ArticleCard variants={itemVariants} whileHover={{ y: -8 }} transition={{ type: 'spring', stiffness: 300 }} onClick={() => onReadMore(item)}>
      <ArticleImage className="article-image" sx={{ backgroundImage: item.featured_image ? `url(${item.featured_image})` : 'url(https://via.placeholder.com/1200x675?text=Featured+News)' }} />
      <Box sx={{ p: { xs: 2, md: 3 }, background: colors.background }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <CategoryChip label={item.category} variant="primary" />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 16, color: colors.textLight }} />
            <Typography variant="caption" sx={{ color: colors.textLight }}>{t('news.estimatedReadTime', { time: item.estimated_read_time })}</Typography>
          </Box>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: colors.text, lineHeight: 1.3, fontSize: { xs: '1.5rem', md: '1.75rem' } }}>{item.title}</Typography>
        <Typography variant="body1" sx={{ color: colors.textLight, mb: 3, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.6 }}>{item.excerpt}</Typography>
        <ReadMoreButton className="read-more-btn" endIcon={<ArrowForwardIcon className="arrow-icon" />}>{t('news.readMore')}</ReadMoreButton>
      </Box>
    </ArticleCard>
  );
};

// NewsListItem Component (Keep as is)
const NewsListItem = ({ item, onReadMore, index }) => {
  const { t, i18n } = useTranslation();
  return (
    <motion.div variants={itemVariants} transition={{ delay: index * 0.1 }}>
      <NewsListItemWrapper onClick={() => onReadMore(item)}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Box sx={{ position: 'relative', paddingTop: '75%', borderRadius: '8px', overflow: 'hidden' }}>
              <Box className="news-image" sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: item.featured_image ? `url(${item.featured_image})` : 'url(https://via.placeholder.com/400x300?text=News+Item)', backgroundSize: 'cover', backgroundPosition: 'center', transition: 'transform 0.5s ease' }} />
            </Box>
          </Grid>
          <Grid item xs={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <CategoryChip label={item.category} size="small" variant={index % 3 === 0 ? 'primary' : index % 3 === 1 ? 'secondary' : 'tertiary'} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarTodayIcon sx={{ fontSize: 14, color: colors.textLight }} />
                <Typography variant="caption" sx={{ color: colors.textLight }}>{new Date(item.first_published_at).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}</Typography>
              </Box>
            </Box>
            <Typography className="news-title" variant="h6" sx={{ fontWeight: 600, mb: 1, color: colors.text, transition: 'color 0.3s ease', fontSize: '1.1rem', lineHeight: 1.3 }}>{item.title}</Typography>
            <Typography variant="body2" sx={{ color: colors.textLight, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 1 }}>{item.excerpt}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="button" sx={{ color: colors.primary, fontSize: '0.875rem', fontWeight: 600 }}>{t('news.readArticle')}</Typography>
              <ChevronRightIcon className="read-more-icon" sx={{ color: colors.primary, fontSize: 20, opacity: 0, transform: 'translateX(-10px)', transition: 'all 0.3s ease' }} />
            </Box>
          </Grid>
        </Grid>
      </NewsListItemWrapper>
      {index < 2 && <Divider sx={{ my: 1 }} /> /* Only add divider after first 2 items in list */}
    </motion.div>
  );
};

// =====================================================================
// Start of NewsSection Component
// =====================================================================
const NewsSection = () => {
  const [newsRef, newsInView] = useSectionVisibility({ threshold: 0.1 });
  const { t, i18n } = useTranslation();
  const [featuredNews, setFeaturedNews] = useState(null);
  const [latestNews, setLatestNews] = useState([]);
  const [allNews, setAllNews] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isArticleDialogOpen, setIsArticleDialogOpen] = useState(false);
  const [isViewAllDialogOpen, setIsViewAllDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null); // ### THIS LINE IS NOW ADDED ###

  useEffect(() => {
    const fetchNews = async () => {
      setIsLoading(true);
      setError(null); // Clear previous errors on new fetch attempt
      console.log("1. fetchNews called. i18n language:", i18n.language);
      try {
        const locale = i18n.language.split('-')[0];
        console.log("2. Locale determined:", locale);

        // Read backend URL from environment variable
        const backendApiBaseUrl = process.env.REACT_APP_BACKEND_API_URL; // Ensure prefix matches your build tool (REACT_APP_ for CRA)
        console.log("3. REACT_APP_BACKEND_API_URL from process.env:", backendApiBaseUrl);

        if (!backendApiBaseUrl) {
          console.error("ERROR: REACT_APP_BACKEND_API_URL is not set in the Vercel environment!");
          setError("Backend API URL is not configured."); // Use the defined setError
          setIsLoading(false);
          return; // Stop execution if URL is missing
        }

        // Construct the full endpoint URL
        const newsApiEndpoint = `${backendApiBaseUrl}/api/news/?locale=${locale}`;
        console.log("4. Attempting to fetch news from:", newsApiEndpoint); // Log the final URL being fetched

        const response = await fetch(newsApiEndpoint);
        console.log("5. Fetch response status:", response.status);

        if (!response.ok) {
          const errorText = await response.text(); // Try to get response text even for errors
          console.error("6. Fetch response not OK. Status:", response.status, "Response text:", errorText);
          // Throw an error to be caught by the catch block
          throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
        }

        const data = await response.json(); // This will fail if the response is HTML
        console.log("7. News data received:", data);

        if (data.items && data.items.length > 0) {
          const sortedNews = data.items.sort((a, b) =>
            new Date(b.first_published_at) - new Date(a.first_published_at)
          );
          setAllNews(sortedNews);
          setFeaturedNews(sortedNews[0]);
          setLatestNews(sortedNews.slice(1, 4));
          console.log("8. News items processed.");
        } else {
          console.log("9. No news items in data or data.items is empty.");
          // You might want to set the news arrays to empty here if needed
           setFeaturedNews(null);
           setLatestNews([]);
           setAllNews([]);
        }
      } catch (error) {
        // This block catches errors from fetch itself (network error) or from response.json()
        console.error('10. Error in fetchNews catch block:', error.message);
        console.error(error); // Log the full error object for more details
        setError(error.message); // Use the defined setError
      } finally {
        setIsLoading(false);
        console.log("11. fetchNews finished.");
      }
    };

    fetchNews();
  }, [i18n.language]); // useEffect dependency array

  // Handler functions (Keep as is)
  const handleReadMore = (article) => {
    setSelectedArticle(article);
    setIsArticleDialogOpen(true);
  };

  const handleViewAll = () => {
    setIsViewAllDialogOpen(true);
  };

  const handleCloseArticle = () => {
    setIsArticleDialogOpen(false);
    setSelectedArticle(null);
  };

  const handleCloseViewAll = (article) => {
    setIsViewAllDialogOpen(false);
    if (article) {
      handleReadMore(article);
    }
  };

  // Optional: Render error message UI
  if (error && !isLoading) {
    return (
      <Box id="news-section" sx={{ py: { xs: 8, md: 10 }, background: colors.backgroundGradient }}>
         <Container maxWidth="xl">
           <Typography color="error" align="center" variant="h6">
             Sorry, could not load news articles. <br/>
             <Typography variant="caption">Error: {error}</Typography>
           </Typography>
         </Container>
      </Box>
    );
  }

  // Main return JSX (Keep structure, skeleton logic, etc., as is)
  return (
    <Box
      ref={newsRef}
      id="news-section"
      sx={{
        py: { xs: 8, md: 10 },
        background: colors.backgroundGradient,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(12, 132, 102, 0.2), transparent)',
          backgroundSize: '200% 100%',
          animation: `${shimmer} 4s linear infinite`,
        },
      }}
    >
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={newsInView ? "visible" : "hidden"}
        >
          <Box sx={{ mb: { xs: 6, md: 8 }, textAlign: 'center' }}>
            <motion.div variants={itemVariants}>
              <Typography variant="overline" sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2, mb: 1, display: 'inline-block', position: 'relative', '&::after': { content: '""', position: 'absolute', bottom: -4, left: '25%', width: '50%', height: '2px', background: `linear-gradient(90deg, transparent, ${colors.primary}, transparent)` } }}>
                {t('news.latestUpdates')}
              </Typography>
            </motion.div>
          </Box>

          {isLoading ? (
            // Loading skeleton (Keep as is)
            <Grid container spacing={4}>
              <Grid item xs={12} md={7}>
                <Box sx={{ height: '100%', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)', background: colors.cardBackground, border: `1px solid ${colors.cardBorder}` }}>
                  <Skeleton variant="rectangular" width="100%" height={300} sx={{backgroundColor: colors.skeletonBackground}} />
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}><Skeleton variant="rounded" width={100} height={30} sx={{backgroundColor: colors.skeletonBackground}}/><Skeleton variant="text" width={120} sx={{backgroundColor: colors.skeletonBackground}}/></Box>
                    <Skeleton variant="text" width="80%" height={60} sx={{ mb: 2, backgroundColor: colors.skeletonBackground }} /><Skeleton variant="text" width="100%" height={20} sx={{ mb: 1, backgroundColor: colors.skeletonBackground }} /><Skeleton variant="text" width="100%" height={20} sx={{ mb: 1, backgroundColor: colors.skeletonBackground }} /><Skeleton variant="text" width="90%" height={20} sx={{ mb: 3, backgroundColor: colors.skeletonBackground }} /><Skeleton variant="text" width={120} height={30} sx={{backgroundColor: colors.skeletonBackground}}/>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}><Skeleton variant="text" width={120} height={30} sx={{backgroundColor: colors.skeletonBackground}}/><Skeleton variant="text" width={100} height={30} sx={{backgroundColor: colors.skeletonBackground}}/></Box>
                {[...Array(3)].map((_, index) => (<Box key={index} sx={{ mb: 2 }}><Grid container spacing={2}><Grid item xs={4}><Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: '8px', backgroundColor: colors.skeletonBackground }} /></Grid><Grid item xs={8}><Skeleton variant="text" width="100%" height={24} sx={{ mb: 1, backgroundColor: colors.skeletonBackground }} /><Skeleton variant="text" width="100%" height={20} sx={{ mb: 1, backgroundColor: colors.skeletonBackground }} /><Skeleton variant="text" width="40%" height={20} sx={{backgroundColor: colors.skeletonBackground}}/></Grid></Grid>{index < 2 && <Divider sx={{ my: 2 }} />}</Box>))}
              </Grid>
            </Grid>
          ) : (
             // Actual Content (Keep as is)
            <Grid container spacing={4}>
              <Grid item xs={12} md={7}>
                {featuredNews ? <FeaturedNewsCard item={featuredNews} onReadMore={handleReadMore} /> : <Typography>No featured news available.</Typography> }
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ color: colors.text, fontWeight: 700, position: 'relative', '&::after': { content: '""', position: 'absolute', bottom: -8, left: 0, width: '40px', height: '2px', background: colors.primary, borderRadius: '2px' } }}>{t('news.latestNews')}</Typography>
                    <Button endIcon={<ArrowForwardIcon className="arrow-icon" />} onClick={handleViewAll} sx={{ color: colors.primary, '&:hover': { backgroundColor: 'transparent', color: colors.secondary, '& .arrow-icon': { transform: 'translateX(4px)' } }, '& .arrow-icon': { transition: 'transform 0.3s ease' } }}>{t('news.viewAll')}</Button>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    {latestNews.length > 0 ? latestNews.map((item, index) => (<NewsListItem key={item.id} item={item} onReadMore={handleReadMore} index={index}/>)) : <Typography>No recent news articles found.</Typography>}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </motion.div>
      </Container>

      {/* Dialogs remain unchanged */}
      <ArticleDialog article={selectedArticle} open={isArticleDialogOpen} onClose={handleCloseArticle}/>
      <ViewAllDialog open={isViewAllDialogOpen} onClose={handleCloseViewAll} articles={allNews}/>
    </Box>
  );
};

export default NewsSection;